import axios from 'axios';
import cuid from 'cuid';
import moment from 'moment';
import React, { useEffect, useState } from 'react';

import { Alert, Autocomplete, Box, Button, Col, DataGrid, DatePicker, MenuItem, Row, Select, TextField, Typography } from 'components/AORedesign';
import { GET_PL_AUTO_COMPLETION_POLICY_ONE_SHIELD, GET_PL_POLICIES_ON_NAME } from '../../constants/api';
import { useDebounce } from '../../hooks/useDebounce';
import { redirectUrlFunction } from '../CommonUtilityServices/RedirectService';

const dataGridResultsColumns = [
  {
    field: 'policy',
    headerName: 'Policy Number',
    sortable: true,
    width: 200,
  },
  {
    field: 'insName',
    headerName: 'Name Insured/Description',
    sortable: true,
    width: 200,
  },
  {
    field: 'address',
    headerName: 'Mailing Address',
    sortable: true,
    width: 200,
  },
  {
    field: 'line',
    headerName: 'LOB',
    sortable: true,
    width: 200,
  },
  {
    field: 'code',
    headerName: 'Agent Code',
    sortable: true,
    width: 200,
  },
  {
    field: 'platform',
    headerName: 'Platform',
    sortable: true,
    width: 200,
  },
  {
    field: 'userID',
    headerName: 'User ID',
    sortable: true,
    width: 200,
  },
];

const PLSPolicyChangeView = () => {
  // const theme = useTheme();
  // const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
  const [amendmentDate, setAmendmentDate] = useState(moment().format('MM/DD/yyyy'));
  const [plPolicyChangeViewSearchType, setPlPolicyChangeViewSearchType] = useState('');
  const [policyNumberValue, setPolicyNumberValue] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [autocompleteInputValues, setAutocompleteInputValues] = useState({
    policyNumber: '',
  });
  const [debouncedState, setDebouncedState] = useDebounce(autocompleteInputValues);
  const [policyNumberOptions, setPolicyNumberOptions] = useState([]);
  const [, setRedirectingToPlPolicyChangeView] = useState(false);
  const [, setErrorPlPolicyChangeViewRedirect] = useState(null);
  const [, setRedirectResponseStatusMessage] = useState(null);
  const [isPolicyChangeRedirectErrorVisible, setIsPolicyChangeRedirectErrorVisible] = useState(false);
  const [policyChangeRedirectError, setPolicyChangeRedirectError] = useState(false);
  const [additionalSearchOptions, setAdditionalSearchOptions] = useState({
    lob: 'all',
    agency: '',
  });
  const [dataGridResults, setDataGridResults] = useState(null);

  const handleChangeAmendmentDate = (date) => {
    setAmendmentDate(date);
  };

  const handleChangePlPolicyChangeViewSearchType = (event) => {
    setPlPolicyChangeViewSearchType(event.target.value);
    setSearchInput('');
  };

  const handleChangeSearchInput = (event) => {
    setSearchInput(event.target.value);
  };

  const handlePolicyNumberOnChange = (event, value) => {
    setPolicyNumberValue(value);
  };

  const handleChangePolicyNumberInput = (event, inputValue) => {
    const sanitizedValue = inputValue.toUpperCase().trim();
    setAutocompleteInputValues({ policyNumber: sanitizedValue });
    setDebouncedState(autocompleteInputValues);
  };

  const handlePolicyNumberOnBlur = () => {
    if (policyNumberValue !== autocompleteInputValues.policyNumber) {
      setPolicyNumberValue(autocompleteInputValues.policyNumber);
    }
  };

  const handleAdditionalSearchOptionsOnChange = (event) => {
    const stateKey = event.target.name;
    const stateValue = event.target.value;
    setAdditionalSearchOptions({ ...additionalSearchOptions, [stateKey]: stateValue });
  };

  const getRequestBody = (policyNumber) => {
    const request_body = {
      Area: 'Personal Lines',
      TypeOfRedirect: 'View / Change Policy',
      DetailedContext: [
        {
          name: 'effectiveDate',
          value: amendmentDate,
        },
        {
          name: 'policyNumber',
          value: policyNumber ?? (searchInput || policyNumberValue),
        },
        {
          name: 'vendorSystem',
          value: false,
        },
      ],
    };
    return request_body;
  };

  const handleClickPlPolicyChangeViewSubmitButton = async () => {
    setRedirectingToPlPolicyChangeView(true);
    setRedirectResponseStatusMessage(null);
    setErrorPlPolicyChangeViewRedirect(null);
    setPolicyChangeRedirectError(false);
    setIsPolicyChangeRedirectErrorVisible(false);

    const sanitizeAndGetRequestPayload = (requestBody) => {
      for (const key in requestBody) {
        if (requestBody[key] === null || requestBody[key] === '' || (key === 'lob' && requestBody[key] === 'all')) {
          delete requestBody[key];
        }
      }
      const requestString = [];

      for (const key in requestBody) {
        requestString.push(`${key}=${requestBody[key]}`);
      }

      return requestString.length ? `?${requestString.join('&')}` : '';
    };

    if (plPolicyChangeViewSearchType === 'policyNumber') {
      const request_body = getRequestBody();
      return redirectUrlFunction(request_body, 'POST', null, setPolicyChangeRedirectError, setIsPolicyChangeRedirectErrorVisible);
    } else if (plPolicyChangeViewSearchType === 'holderLastName') {
      try {
        const requestBody = {
          lastName: searchInput,
          lob: additionalSearchOptions.lob,
          agency: additionalSearchOptions.agency,
        };
        const queryRequestParams = sanitizeAndGetRequestPayload(requestBody);

        const requestUrl = `${GET_PL_POLICIES_ON_NAME}${queryRequestParams}`;

        const response = await axios.get(requestUrl);
        if (response.status === 200) {
          setDataGridResults(response.data);
        }
      } catch (error) {
        setPolicyChangeRedirectError(error.message);
        setIsPolicyChangeRedirectErrorVisible(true);
      }
    }
  };

  const handleOnRowDoubleClick = (params) => {
    const request_body = getRequestBody(params.row.policy);
    redirectUrlFunction(request_body, 'POST', null, setPolicyChangeRedirectError, setIsPolicyChangeRedirectErrorVisible);
  };

  const fetchPolicyNumberSuggestions = async (policyNumberInput) => {
    const { data } = await axios.get(`${GET_PL_AUTO_COMPLETION_POLICY_ONE_SHIELD}${policyNumberInput}`);
    return data;
  };

  useEffect(async () => {
    if (autocompleteInputValues.policyNumber.length >= 3) {
      const suggestions = await fetchPolicyNumberSuggestions(autocompleteInputValues.policyNumber).catch((err) => {
        console.error(err);
      });
      setPolicyNumberOptions(suggestions);
    } else {
      setPolicyNumberOptions([]);
    }
  }, [debouncedState.policyNumber]);

  const isPolicyNumberSearchType = plPolicyChangeViewSearchType === 'policyNumber';
  const isDefaultSearchType = plPolicyChangeViewSearchType !== 'policyNumber';
  const isSubmitDisabled = plPolicyChangeViewSearchType === '';
  const showAdditionalSearchOptions = plPolicyChangeViewSearchType === 'holderLastName';
  const additionalSearchOptionsLOB = additionalSearchOptions.lob;
  const additionalSearchOptionsAgency = additionalSearchOptions.agency;

  return (
    <>
      <Box id={'PLSPolicyChangeViewHeaderBoxumn'}>
        <Typography paragraph={true}>Enter the following information if doing a policy change, inquiry, or cancellation.</Typography>
      </Box>
      <Box id={'PLSPolicyChangeViewDatePickerBoxumn'}>
        <Box id={'PLSPolicyChangeViewSpacerForDate'} mb={2}>
          <DatePicker id={'PLSPolicyChangeViewDatePicker'} label={'Amendment Date'} value={amendmentDate} onChange={handleChangeAmendmentDate} />
        </Box>
      </Box>
      <Box id={'PLSPolicyChangeViewFormElementsBoxumn'}>
        <Box id={'PLSPolicyChangeViewSpacerForFormElements'} mb={2}>
          <Row id={'PLSPolicyChangeViewSelectRow'} spacing={2} mb={2}>
            <Col id={'PLSPolicyChangeViewSelectCol'}>
              <Select
                id={'pl-policy-change-view-search-type'}
                labelId={'pl-policy-change-view-search-type-policy-number'}
                label={'Specific Search'}
                value={plPolicyChangeViewSearchType}
                onChange={handleChangePlPolicyChangeViewSearchType}>
                <MenuItem id={'PLSPolicyChangeViewDefaultMenuItem'} name={''} value={''}>
                  Select Search Type
                </MenuItem>
                <MenuItem id={'PLSPolicyChangeViewPolicyNumberMenuItem'} name={'policyNumber'} value={'policyNumber'}>
                  Policy Number
                </MenuItem>
                <MenuItem id={'PLSPolicyChangeViewPolicyLastNameMenuItem'} name={'holderLastName'} value={'holderLastName'}>
                  Policy Holder Last Name
                </MenuItem>
              </Select>
            </Col>

            {isPolicyNumberSearchType && (
              <Col id={'PLSPolicyChangeViewSearchInputCol'}>
                <Autocomplete
                  id={'PLSPolicyChangeViewPolicyNumberInput'}
                  disableClearable
                  filterOptions={(options) => options}
                  value={policyNumberValue}
                  options={policyNumberOptions ?? []}
                  onInputChange={handleChangePolicyNumberInput}
                  onChange={handlePolicyNumberOnChange}
                  onBlur={handlePolicyNumberOnBlur}
                  style={{ width: 300 }}
                  renderInput={(params) => <TextField {...params} label={'Enter Search Input'} />}
                />
              </Col>
            )}

            {isDefaultSearchType && (
              <Col id={'PLSPolicyChangeViewSearchInputCol'}>
                <TextField id={'PLSPolicyChangeViewSearchInput'} label={'Enter Search Input'} value={searchInput} onChange={handleChangeSearchInput} />
              </Col>
            )}
          </Row>

          {showAdditionalSearchOptions && (
            <>
              <Box my={2}>
                <Typography>Additional Search Options</Typography>
              </Box>
              <Row spacing={2} mb={2}>
                <Col>
                  <Select
                    id={'pl-policy-change-view-last-name-lob'}
                    labelId={'pl-policy-change-view-last-name-lob-label'}
                    label={'Line Of Business'}
                    name={'lob'}
                    value={additionalSearchOptionsLOB}
                    onChange={handleAdditionalSearchOptionsOnChange}>
                    <MenuItem id={'pl-policy-change-view-last-name-lob-menu-item-all'} name={'all'} value={'all'}>
                      All
                    </MenuItem>
                    <MenuItem id={'pl-policy-change-view-last-name-lob-menu-item-auto'} name={'AU'} value={'AU'}>
                      Automobile
                    </MenuItem>
                    <MenuItem id={'pl-policy-change-view-last-name-lob-menu-item-home'} name={'HO'} value={'HO'}>
                      Homeowner
                    </MenuItem>
                  </Select>
                </Col>
                <Col>
                  <TextField label={'Agent Code:'} name={'agency'} value={additionalSearchOptionsAgency} onChange={handleAdditionalSearchOptionsOnChange} />
                </Col>
              </Row>
            </>
          )}
        </Box>
      </Box>
      <Box id={'PLSPolicyChangeViewAdditionalFieldsBox'}>
        {plPolicyChangeViewSearchType === 'plChangeViewLastName' && (
          <>
            <Box id={'PLSPolicyChangeViewAdditionalSearchCriteriaBox'}>
              <Box id={'PLSPolicyChangeViewSpacerForAdditionalSearch'} mb={2}>
                <Typography id={'PLSPolicyChangeViewAdditionalSearch'} gutterBottom={true}>
                  Additional Search Criteria
                </Typography>
              </Box>
            </Box>
            <Select id={'PLSPolicyChangeViewSelectAll'}>
              <MenuItem id={'PLSPolicyChangeViewMenuItemAll'} value={'plPolicyChangeViewMenuItemAll'}>
                All
              </MenuItem>
              <MenuItem id={'PLSPolicyChangeViewMenuItemAU'} value={'plPolicyChangeViewMenuItemAutomobile'}>
                Automobile
              </MenuItem>
              <MenuItem id={'PLSPolicyChangeViewMenuItemHO'} value={'plPolicyChangeViewMenuItemHomeowner'}>
                HomeOwner
              </MenuItem>
            </Select>
          </>
        )}
      </Box>
      <Box id={'PLSPolicyChangeViewButtonBox'}>
        {isPolicyChangeRedirectErrorVisible && <Alert id={'plsPolicyChangeErrorAlertComponent'} severity={'error'} message={policyChangeRedirectError} />}
        <Button
          id={'PLSPolicyChangeSubmitButton'}
          variant={'contained'}
          color={'primary'}
          disabled={isSubmitDisabled}
          onClick={handleClickPlPolicyChangeViewSubmitButton}>
          Submit
        </Button>
      </Box>
      <Box mb={2}></Box>
      <DataGrid
        autoHeight={true}
        pageSize={25}
        rowsPerPageOptions={[]}
        rows={dataGridResults?.map((row) => ({
          id: cuid(),
          ...row,
        }))}
        columns={dataGridResultsColumns}
        onRowDoubleClick={handleOnRowDoubleClick}
      />
    </>
  );
};

export default PLSPolicyChangeView;
